import {Component, OnInit} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {FilterValue, ListView, TypeComparaison, TypeFilter} from "@domain/common/list-view";
import {OpeLogError, EtatCarte} from "@domain/admin/ca/ope-log-error";
import {Sorting} from "@domain/common/list-view/sorting";
import {SuiviReleveErrorListItemComponent} from "./suivi-releve-error-list-item.component";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {SuiviReleveErrorDetailComponent} from "./suivi-releve-error-detail.component";
import {FloatingButtonAction, TypeAction} from "@share/component/floating-button/floating-button";
import {BehaviorSubject, combineLatest} from "rxjs";
import {SuiviReleveServiceHttp} from "@services/admin/ca/suivi-releve-service-http.service";
import {ToastrService} from "ngx-toastr";
import {TypeCodeErreur} from "@domain/common/http/result";
import {take} from "rxjs/operators";

/**
 * Composant de la liste des erreurs du suivi des relevés
 *
 * @author Guillaume TRAVOT
 * @date 31/03/2026
 */
@Component({
    host: {'data-test-id': 'suivi-releve-error-liste'},
    selector: 'suivi-releve-error-liste',
    templateUrl: './suivi-releve-error-liste.component.html'
})
export class SuiviReleveErrorListeComponent implements OnInit {
    /** ID du lot */
    idLot: number;

    /** Indique si c'est la dernière opération */
    isLastOpe: boolean = true;

    /** Liste des erreurs */
    listeOpeLogError: ListView<OpeLogError, SuiviReleveErrorListItemComponent>;

    /** Liste des actions */
    listeActions = new BehaviorSubject<FloatingButtonAction[]>([]);

    /** Types d'erreurs possibles */
    private typesErreur = [
        { code: EtatCarte.BAD_CONTRAT_REFERENCE, libelle: 'admin.frais.cartes.suivi.erreur.BAD_CONTRAT_REFERENCE', solution: 'admin.frais.cartes.suivi.solution.BAD_CONTRAT_REFERENCE' },
        { code: EtatCarte.CARTE_DIVERGE_CONTRAT, libelle: 'admin.frais.cartes.suivi.erreur.CARTE_DIVERGE_CONTRAT', solution: 'admin.frais.cartes.suivi.solution.CARTE_DIVERGE_CONTRAT' },
        { code: EtatCarte.BAD_CARTE_REFERENCE, libelle: 'admin.frais.cartes.suivi.erreur.BAD_CARTE_REFERENCE', solution: 'admin.frais.cartes.suivi.solution.BAD_CARTE_REFERENCE' },
        { code: EtatCarte.BAD_MEDOC_REFERENCE, libelle: 'admin.frais.cartes.suivi.erreur.BAD_MEDOC_REFERENCE', solution: 'admin.frais.cartes.suivi.solution.BAD_MEDOC_REFERENCE' },
        { code: EtatCarte.BAD_MCC_REFERENCE, libelle: 'admin.frais.cartes.suivi.erreur.BAD_MCC_REFERENCE', solution: 'admin.frais.cartes.suivi.solution.BAD_MCC_REFERENCE' },
        { code: EtatCarte.DOUBLON, libelle: 'admin.frais.cartes.suivi.erreur.DOUBLON', solution: null },
        { code: EtatCarte.RETRAIT_DE, libelle: 'admin.frais.cartes.suivi.erreur.RETRAIT_DE', solution: null }
    ];

    /**
     * Constructeur
     */
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private translateService: TranslateService,
        private matDialog: MatDialog,
        private suiviReleveServiceHttp: SuiviReleveServiceHttp,
        private toastrService: ToastrService
    ) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        combineLatest([this.route.params, this.route.queryParams])
            .pipe(take(1))
            .subscribe(([params, queryParams]) => {
                this.idLot = +params['idLot'];
                if (queryParams['isLastOpe'] !== undefined) {
                    this.isLastOpe = queryParams['isLastOpe'] === 'true';
                }
                this.initListe();
                this.initListeActions();
            });
    }

    /**
     * Initialisation de la liste des erreurs
     */
    private initListe(): void {
        this.listeOpeLogError = new ListView<OpeLogError, SuiviReleveErrorListItemComponent>({
            uri: `/controller/ImportCA/listeOpeLogError/${this.idLot}`,
            title: this.translateService.instant('admin.frais.cartes.suivi.detailLot', { idLot: this.idLot }),
            component: SuiviReleveErrorListItemComponent,
            defaultOrder: 'idOpeCarte',
            isFilter: true,
            extraOptions: {
                openDetail: (error: OpeLogError) => this.openDetail(error),
                typesErreur: this.typesErreur
            },
            listeFilters: [
                {
                    clef: 'erreur',
                    title: this.translateService.instant('admin.frais.cartes.suivi.anomalie'),
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    listeValues: this.typesErreur.map(t => ({
                        code: t.code.toString(),
                        libelle: this.translateService.instant(t.libelle)
                    } as FilterValue)),
                    multiple: true
                },
                {
                    clef: 'opeCarte.idOpeCarte',
                    title: this.translateService.instant('admin.frais.cartes.suivi.referenceInterne'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    type: TypeFilter[TypeFilter.STRING]
                },
                {
                    clef: 'opeCarte.carte.user.matricule',
                    title: this.translateService.instant('admin.frais.cartes.suivi.matricule'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    type: TypeFilter[TypeFilter.STRING]
                },
                {
                    clef: 'opeCarte.montantAchat',
                    title: this.translateService.instant('admin.frais.cartes.suivi.montant'),
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    type: TypeFilter[TypeFilter.DECIMAL]
                }
            ]
        });

        //Définition des colonnes de la liste et du tri
        this.listeOpeLogError.columns = [
            {key: 'erreur', title: this.translateService.instant('admin.frais.cartes.suivi.anomalie')},
            {key: 'idOpeCarte', title: this.translateService.instant('admin.frais.cartes.suivi.referenceInterne')},
            {key: 'collab', title: this.translateService.instant('admin.frais.cartes.suivi.collaborateur')},
            {key: 'montant', title: this.translateService.instant('admin.frais.cartes.suivi.montant')}
        ];
        this.listeOpeLogError.sorting = new Sorting(this.listeOpeLogError.columns, 'idOpeCarte');
    }

    /**
     * Initialisation des actions possibles
     */
    private initListeActions(): void {
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-archive',
            libelle: 'admin.frais.cartes.suivi.reintegrer',
            tooltip: () => this.translateService.instant('admin.frais.cartes.suivi.reintegrerTooltip'),
            doAction: () => this.reintegrer(),
            isVisible: () => this.isLastOpe && this.listeOpeLogError?.data?.listeResultats?.length > 0
        }]);
    }

    /**
     * Ouvre la fenêtre de détail d'une erreur
     */
    openDetail(error: OpeLogError): void {
        this.matDialog.open<SuiviReleveErrorDetailComponent, any, boolean>(SuiviReleveErrorDetailComponent, {
            data: {
                idOpeCarte: error.idOpeCarte,
                typesErreur: this.typesErreur
            },
            minWidth: 800
        }).afterClosed().subscribe(refresh => {
            if (refresh) {
                this.listeOpeLogError.refresh();
            }
        });
    }

    /**
     * Réintègre le lot
     */
    reintegrer(): void {
        this.suiviReleveServiceHttp.reintegrerLot(this.idLot).subscribe({
            next: (result) => {
                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(this.translateService.instant('admin.frais.cartes.suivi.reintegrationSuccess'));
                    this.goBack();
                } else {
                    TypeCodeErreur.showError(result.codeErreur, this.translateService, this.toastrService);
                }
            },
            error: () => {
                this.toastrService.error(this.translateService.instant('admin.frais.cartes.suivi.reintegrationError'));
            }
        });
    }

    /**
     * Retourne à la liste des relevés
     */
    goBack(): void {
        this.router.navigate(['Admin/Frais/Ndf/SuiviReleves']);
    }
}